/**
 * Mode concours : parcourir les retouches et voter pour la meilleure.
 *
 * Tout le monde a redessine LA MEME zone du MEME tableau. Le plateau montre donc
 * une retouche a la fois, en grand, dans son œuvre : c'est la seule facon de
 * juger un raccord, et c'est aussi pour cela qu'on ne les met pas cote a cote en
 * grand.
 *
 * Trois decisions d'interface, chacune contre une facon de rater ce mode :
 *
 *  - le defile est MANUEL. Aucune minuterie ne fait avancer la vue : on compare
 *    en revenant en arriere, et une rotation automatique arracherait l'image des
 *    yeux au moment ou l'on se decide.
 *  - regarder n'est pas voter. La pellicule et les fleches ne font que changer
 *    ce qu'on regarde ; le vote demande un bouton, qui dit lequel tient
 *    actuellement la voix. Sans cette separation, parcourir les retouches
 *    reviendrait a voter pour chacune au passage.
 *  - pendant le vote, les auteurs sont ANONYMES, seule sa propre retouche est
 *    nommee (et elle n'est pas votable). On juge un dessin, pas une amitie. Les
 *    noms sortent a la revelation, avec le depouillement.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { HuntTarget, RoomSnapshot } from '@retusch/shared';
import { HuntEngine, type HuntSlot } from './HuntEngine.js';
import { loadArtwork } from './artwork.js';
import { useT } from '../locales/useT.js';
import { playerById } from '../store/roomStore.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { CheckIcon, ChevronLeftIcon } from '../ui/icons.js';
import { observeSize } from './canvas/observeSize.js';
import { renderThumbnail } from '../gallery/renderArtwork.js';

/** Cote des miniatures de la pellicule, en pixels. */
const THUMB_PX = 96;

export interface ContestBoardProps {
  readonly room: RoomSnapshot;
  readonly file: string;
  readonly targets: readonly HuntTarget[];
  readonly drawings: Readonly<Record<number, ArrayBuffer>>;
  readonly selfId: string | null;
  /** Mon vote, tel que le serveur l'a confirme. */
  readonly myVote: number | null;
  /** Depouillement, a la revelation seulement. Null pendant le vote. */
  readonly tally: Readonly<Record<number, number>> | null;
  readonly durationMs: number;
  readonly onVote: (slot: number) => void;
}

export function ContestBoard({
  room,
  file,
  targets,
  drawings,
  selfId,
  myVote,
  tally,
  durationMs,
  onVote,
}: ContestBoardProps): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<HuntEngine | null>(null);
  const results = tally !== null;

  // Les blobs arrivent un par un apres le JSON. Tant qu'il en manque un, sa
  // retouche serait une zone blanche, donc un candidat injustement disqualifie.
  const complete = targets.every((target) => drawings[target.slot] !== undefined);

  const slots = useMemo<readonly HuntSlot[] | null>(() => {
    if (!complete) return null;
    return targets.map((target) => ({
      slot: target.slot,
      drawerId: target.drawerId,
      zone: target.zone,
      palette: target.palette,
      drawing: drawings[target.slot] ?? null,
      own: target.drawerId === selfId,
      label: '',
    }));
    // `drawings` volontairement hors dependances : une fois complet, seul son
    // identite change, pas son contenu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, complete, selfId]);

  const [current, setCurrent] = useState(0);
  const entry = targets[current];

  // A la revelation, on ouvre sur la retouche qui a gagne : c'est ce que tout le
  // monde vient voir.
  useEffect(() => {
    if (tally === null) return;
    let best = 0;
    let bestCount = -1;
    targets.forEach((target, index) => {
      const count = tally[target.slot] ?? 0;
      if (count > bestCount) {
        best = index;
        bestCount = count;
      }
    });
    setCurrent(best);
  }, [tally, targets]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || slots === null) return;

    let alive = true;
    let engine: HuntEngine | null = null;

    void loadArtwork(file)
      .then((image) => {
        if (!alive) return;
        engine = new HuntEngine({
          canvas,
          image,
          slots,
          // Le canvas ne sert qu'a REGARDER : le vote passe par un bouton, un
          // clic sur l'image ne doit rien declencher.
          onStrike: () => undefined,
          canStrike: () => false,
        });
        engineRef.current = engine;
        engine.start();
        /**
         * LE TABLEAU AFFICHE, tout de suite.
         *
         * Le moteur nait de facon ASYNCHRONE, apres le chargement de l'image :
         * l'effet qui appelle `showOnly` s'etait deja execute, sur un
         * `engineRef` encore vide. Faute d'avoir recu la consigne, le moteur
         * peignait TOUTES les retouches, qui occupent ici la meme zone : on
         * voyait donc la derniere, sous le pseudo de la premiere. Le defaut ne
         * se voyait qu'a l'ouverture, la premiere fleche remettant tout
         * d'aplomb, ce qui le rendait d'autant plus deroutant.
         */
        engine.showOnly(shownRef.current);
        return observeSize(canvas, () => engine?.resize());
      })
      .catch(() => undefined);

    return () => {
      alive = false;
      engine?.destroy();
      engineRef.current = null;
    };
  }, [file, slots]);

  // Une seule retouche a l'ecran : elles occupent toutes la meme zone, les
  // peindre ensemble ne montrerait que la derniere.
  const shownRef = useRef<number | null>(entry?.slot ?? null);
  shownRef.current = entry?.slot ?? null;

  useEffect(() => {
    engineRef.current?.showOnly(entry?.slot ?? null);
  }, [entry?.slot, slots]);

  const move = useCallback(
    (step: number) => {
      setCurrent((index) => {
        const count = targets.length;
        if (count === 0) return 0;
        return (index + step + count) % count;
      });
    },
    [targets.length],
  );

  // Fleches du clavier : parcourir sans quitter la souris de l'image.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement) return;
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move]);

  const own = entry?.drawerId === selfId;
  const voted = entry !== undefined && myVote === entry.slot;
  const author = playerById(room, entry?.drawerId ?? null);

  return (
    <div className="contest">
      <div className="board">
        <canvas ref={canvasRef} className="board__canvas board__canvas--grab" />

        <div className="board__hud">
          <span className="contest__position mono-tabular">
            {t('contest.entry', { n: current + 1, total: targets.length })}
          </span>
          <span className="board__timer">
            <PhaseTimer endsAt={room.endsAt} durationMs={durationMs} />
          </span>
        </div>

        {/* Fleches posees SUR l'image, aux deux bords : c'est la ou va la main
            quand on compare, et elles ne prennent aucune hauteur au tableau. */}
        <button
          type="button"
          className="contest__arrow contest__arrow--prev"
          aria-label={t('contest.previous')}
          onClick={() => move(-1)}
        >
          <ChevronLeftIcon size={22} />
        </button>
        <button
          type="button"
          className="contest__arrow contest__arrow--next"
          aria-label={t('contest.next')}
          onClick={() => move(1)}
        >
          <ChevronLeftIcon size={22} />
        </button>
      </div>

      {/* Verdict de l'entree courante : son auteur et ses voix a la
          revelation, son statut pendant le vote. */}
      <div className="contest__bar">
        {results ? (
          <span className="contest__author">
            {author?.nickname ?? '?'}
            <strong className="contest__count mono-tabular">
              {t('contest.votes', { n: tally?.[entry?.slot ?? -1] ?? 0 })}
            </strong>
          </span>
        ) : own ? (
          <span className="contest__own">{t('contest.yours')}</span>
        ) : (
          <button
            type="button"
            className={voted ? 'button-ghost contest__voted' : 'button-primary'}
            aria-pressed={voted}
            onClick={() => entry !== undefined && onVote(entry.slot)}
          >
            {voted ? (
              <>
                <CheckIcon size={16} />
                {t('contest.voted')}
              </>
            ) : (
              t('contest.vote')
            )}
          </button>
        )}
      </div>

      <ContestStrip
        room={room}
        file={file}
        targets={targets}
        drawings={drawings}
        selfId={selfId}
        myVote={myVote}
        tally={tally}
        current={current}
        onSelect={setCurrent}
      />
    </div>
  );
}

/**
 * Pellicule des retouches : le seul moyen de savoir combien il en reste a voir,
 * et de revenir sur celle qu'on avait aimee. Elle n'affiche AUCUN nom pendant le
 * vote.
 */
function ContestStrip({
  room,
  file,
  targets,
  drawings,
  selfId,
  myVote,
  tally,
  current,
  onSelect,
}: {
  room: RoomSnapshot;
  file: string;
  targets: readonly HuntTarget[];
  drawings: Readonly<Record<number, ArrayBuffer>>;
  selfId: string | null;
  myVote: number | null;
  tally: Readonly<Record<number, number>> | null;
  current: number;
  onSelect: (index: number) => void;
}): ReactElement {
  const t = useT();
  const [thumbs, setThumbs] = useState<ReadonlyMap<number, string>>(() => new Map());
  const rendered = useRef<Set<number>>(new Set());

  useEffect(() => {
    let alive = true;
    for (const target of targets) {
      const blob = drawings[target.slot] ?? null;
      if (blob === null || rendered.current.has(target.slot)) continue;
      rendered.current.add(target.slot);

      void renderThumbnail(
        { file, zone: target.zone, palette: target.palette, drawing: blob },
        THUMB_PX,
      )
        .then((url) => {
          if (alive) setThumbs((all) => new Map(all).set(target.slot, url));
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [targets, drawings, file]);

  return (
    <div className="filmstrip" role="tablist" aria-label={t('contest.title')}>
      {targets.map((target, index) => {
        const url = thumbs.get(target.slot);
        const own = target.drawerId === selfId;
        const author = playerById(room, target.drawerId);
        return (
          <button
            key={target.slot}
            type="button"
            role="tab"
            aria-selected={index === current}
            className="filmstrip__item"
            data-active={index === current}
            data-voted={myVote === target.slot}
            onClick={() => onSelect(index)}
          >
            <span className="filmstrip__frame">
              {url !== undefined && <img className="filmstrip__thumb" src={url} alt="" />}
              {myVote === target.slot && (
                <span className="filmstrip__vote" aria-hidden="true">
                  <CheckIcon size={12} />
                </span>
              )}
            </span>
            {/* Anonyme pendant le vote : seule sa propre retouche se nomme,
                pour ne pas voter pour soi sans le savoir. */}
            <span className="filmstrip__name">
              {tally !== null
                ? `${author?.nickname ?? '?'} · ${tally[target.slot] ?? 0}`
                : own
                  ? t('common.you')
                  : `#${index + 1}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
